"use client";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import { Button } from "@cmp-src/primitives/button";
import { Spoiler } from "@cmp-src/primitives/spoiler";
import { QuickReserveButton } from "@cmp-src/reserve/QuickReserveButton";
import {
  AvailabilityErrorCode,
  AvailabilityErrorDetails,
  IResource
} from "@gettactic/api";
import { UseDisclosureReturn } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { formatApiDatetime } from "@gettactic/helpers/src/date-time/formatApiDatetime";
import { isRBFeature } from "@gettactic/helpers/src/featureFlags";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { forceReloadChrome96Bug } from "@gettactic/helpers/src/map/svg/forceReloadChrome96Bug";
import { InteractiveZone } from "@gettactic/helpers/src/map/types";
import { canQuickReserve } from "@gettactic/helpers/src/reserve/canQuickReserve";
import { timeRelatedErrorCodes } from "@gettactic/helpers/src/reserve/timeRelatedErrorCodes";
import { getReservationExtensionDays } from "@gettactic/helpers/src/resources/defines";
import { isResourceTicketGated } from "@gettactic/helpers/src/resources/isResourceTicketGated";
import { shouldEnablePrivacyForResourceType } from "@gettactic/helpers/src/resources/utils";
import {
  IResourceWithSchedules,
  useAreas,
  useAuthenticated,
  useMapSchedulable,
  useOrgFeatureFlag,
  useSettings
} from "@gettactic/hooks";
import { XIcon } from "@heroicons/react/outline";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Calendar, MapPin, Plus } from "lucide-react";
import React, {
  CSSProperties,
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { DedicatedReleases } from "@/features/map/components/DedicatedReleases";
import { EditMapResourceModal } from "@/features/map/components/EditMapResourceModal";
import { MapPopoverShell } from "@/features/map/components/MapPopoverShell";
import { OpenHourRulesNotice } from "@/features/map/components/OpenHourRulesNotice";
import {
  infoBoxIcon,
  PopoverInfoBox
} from "@/features/map/components/PopoverInfoBox";
import { ResourceAmenityChips } from "@/features/map/components/ResourceAmenityChips";
import { ResourceAssignments } from "@/features/map/components/ResourceAssignments";
import { ResourceStatusBand } from "@/features/map/components/ResourceStatusBand";
import { SchedulesGroups } from "@/features/map/components/SchedulesGroups";
import {
  getResourceStatus,
  hasResourceAssignment,
  isResourceOutOfService,
  isResourceRestricted,
  withVisibleAssignments
} from "@/features/map/helpers/resourceStatus";
import { useMap } from "@/features/map/MapProvider";
import { ResourceSettingsPanel } from "@/features/reserve/components/ResourceSettingsPanel/ResourceSettingsPanel";
import { RestrictionAlert } from "@/features/reserve/components/RestrictionAlert/RestrictionAlert";
import { useEditResourceRouter } from "@/features/reserve/hooks/useEditResourceRouter";
import { useQuickReserve } from "@/features/reserve/hooks/useQuickReserve";
import type { NewRequestResourceContext } from "@/features/workplace-requests/views/queue/NewRequestWizard";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { canScheduleOthers } from "@/lib/permissions";
import { getResourceTypeName, isAllowedToReserve } from "@/lib/utils";
import { OptionsMenu } from "@/ui/controls/OptionsMenu";
import { Modal, TextEditorContent } from "@/ui/index";

// Lazy-loaded to break the import cycle (the wizard pulls in the map, which renders this).
const NewRequestWizard = React.lazy(() =>
  import("@/features/workplace-requests/views/queue/NewRequestWizard").then(
    (m) => ({ default: m.NewRequestWizard })
  )
);

type ResourceDetailsProps = {
  setOpenResourceModal: Dispatch<SetStateAction<boolean>>;
  setSelectedResource: Dispatch<SetStateAction<IResource | null>>;
  openResourceModal: boolean;
  startDate: Date;
  endDate: Date | null;
  formattedDate: string;
  res: IResourceWithSchedules | null;
  zones: InteractiveZone[];
  floor: IResource;
  openReservation: (res: IResource) => void;
  resourcePermission: boolean;
  resources: IResourceWithSchedules[];
  setEditGroupId: Dispatch<SetStateAction<string | null>>;
  officeId: string;
  editGroupId: string | null;
  svg: SVGSVGElement;
  setMapChanges: Dispatch<SetStateAction<number>>;
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  styles: { [p: string]: CSSProperties };
  attributes: { [p: string]: { [p: string]: string } | undefined };
  officeTimeZone: string;
  meetingsDisabled: boolean;
  embedded?: boolean;
  isReservationMode?: boolean;
  fullscreenMapDisclosure?: UseDisclosureReturn;
};

export const ResourceDetails: React.FC<ResourceDetailsProps> = React.memo(
  ({
    res,
    floor,
    openReservation,
    openResourceModal,
    setOpenResourceModal,
    startDate,
    endDate,
    resourcePermission,
    resources,
    editGroupId,
    officeId,
    setEditGroupId,
    svg,
    setMapChanges,
    setPopperElement,
    styles,
    attributes,
    officeTimeZone,
    meetingsDisabled,
    embedded,
    setSelectedResource,
    isReservationMode = false,
    fullscreenMapDisclosure
  }) => {
    const {
      userContext: { authenticatedUser }
    } = useAuthenticated();
    const { timeMode, events } = useMap();
    const allowReserveOnBehalf = canScheduleOthers(authenticatedUser);
    const offices = authenticatedUser.offices;
    const { openResourceId, isSideDrawerOpen, setIsSideDrawerOpen } =
      useEditResourceRouter(officeId, res?.type ? [res?.type] : []);

    const { isBreakpoint } = useBreakpoint();
    const isMd = isBreakpoint("md");

    const response = useMapSchedulable({
      officeId: officeId,
      parentId: floor ? floor.id : null,
      startDate: formatApiDatetime(startDate),
      endDate: endDate ? formatApiDatetime(endDate) : undefined,

      // only ever read for the selected resource
      enabled: !!res?.id
    });

    let availabilityErrors:
      | {
          reasonCode: AvailabilityErrorCode;
          details?: AvailabilityErrorDetails;
        }[]
      | undefined;
    if (response.nonAvailable.length > 0) {
      availabilityErrors = response.nonAvailable
        .filter((r) => r.resource.id === res?.id)
        .map((r) => {
          return {
            reasonCode: r.reason_code as AvailabilityErrorCode,
            details: r.details as AvailabilityErrorDetails | undefined
          };
        });
    }

    const queryClient = useQueryClient();
    const [editMapPointModal, setEditMapPointModal] = useState(false);
    const workplaceRequestsEnabled = useOrgFeatureFlag("workplace_requests");
    const [newRequestResource, setNewRequestResource] =
      useState<IResource | null>(null);
    const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
    // Which entry point opened the wizard: booking a gated resource restricts the
    // picker to its configured types, while "New request" must still reach every
    // type — reporting a broken desk is not booking it.
    const [isGatedBooking, setIsGatedBooking] = useState(false);
    const entity = res?.type ? res.type : "desk";
    const office =
      offices?.officeId && res?.office_id
        ? offices?.byId[res?.office_id]
        : undefined;
    const closeModal = useCallback(() => {
      setOpenResourceModal(false);
    }, [setOpenResourceModal]);
    const openRequestWizardFor = useCallback(
      (resource: IResource, restrictToGatedTypes: boolean) => {
        // Capture the resource so the wizard keeps its context after the popup closes.
        setNewRequestResource(resource);
        setIsGatedBooking(restrictToGatedTypes);
        closeModal();
        setIsNewRequestOpen(true);
      },
      [closeModal]
    );
    const openRequestWizard = useCallback(
      (restrictToGatedTypes: boolean) => {
        if (!res) return;
        openRequestWizardFor(res, restrictToGatedTypes);
      },
      [res, openRequestWizardFor]
    );
    // The reserve card refuses gated resources coming from any "start
    // reservation" path (sidebar list, search, deep link) and hands them over
    // here, so they get the same request form as the popover's "Request to Book".
    useEffect(() => {
      const onRequestGatedBooking = (resource: IResource) =>
        openRequestWizardFor(resource, true);
      events.register("onRequestGatedBooking", onRequestGatedBooking);
      return () => {
        events.unregister("onRequestGatedBooking", onRequestGatedBooking);
      };
    }, [events, openRequestWizardFor]);
    const openNewRequest = useCallback(
      () => openRequestWizard(false),
      [openRequestWizard]
    );
    const openGatedBooking = useCallback(
      () => openRequestWizard(true),
      [openRequestWizard]
    );
    // A gated resource is bookable only by filing one of its request types —
    // the direct reservation flow is closed off for it entirely.
    const isTicketGated = isResourceTicketGated(res);

    const openEditMapPointModal = useCallback(() => {
      closeModal();
      setEditMapPointModal(true);
    }, [closeModal, setEditMapPointModal]);
    const openEditModal = useCallback(() => {
      closeModal();
      if (res?.id) {
        openResourceId(res?.id);
      }
    }, [closeModal, openResourceId, res]);
    useEffect(() => {
      const keyBinding = (e: KeyboardEvent) => {
        if (!resourcePermission || !openResourceModal) {
          return;
        }
        if (e.key === "e" && !isSideDrawerOpen) {
          openEditModal();
        } else if (editGroupId && e.key === "m" && !editMapPointModal) {
          openEditMapPointModal();
        }
      };
      document.addEventListener("keydown", keyBinding);
      return () => document.removeEventListener("keydown", keyBinding);
    }, [
      isSideDrawerOpen,
      editMapPointModal,
      editGroupId,
      openResourceModal,
      openEditMapPointModal,
      openEditModal,
      resourcePermission
    ]);
    const allowReserveByMaxDate = useMemo(() => {
      return isAllowedToReserve(
        startDate,
        officeTimeZone,
        office,
        entity,
        authenticatedUser.user?.organization_user?.roles,
        getReservationExtensionDays(authenticatedUser.user?.email)
      );
    }, [startDate, officeTimeZone, office, entity]);

    const allowReserve = useMemo(() => {
      if (!res || !res.is_available) {
        return false;
      }
      // If the user can reserve on behalf of others, we need to be able to trigger the reserve modal
      // with the resource pre-selected.
      if (allowReserveOnBehalf) {
        return true;
      }

      return (
        allowReserveByMaxDate &&
        res.available_slots > 0 &&
        (res.type !== "meeting_room" || !meetingsDisabled)
      );
    }, [allowReserveByMaxDate, allowReserveOnBehalf, meetingsDisabled, res]);

    const clickOutsideRef = useOnclickOutside(closeModal);
    // The popover root is both the popper-positioned element and the
    // click-outside boundary, so merge both refs onto it.
    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        setPopperElement(node);
        clickOutsideRef(node);
      },
      [setPopperElement, clickOutsideRef]
    );

    // The map wrapper that owns this popover's svg. Used to render the request
    // wizard contained (backdrop scoped to the map) instead of over the viewport.
    const mapContainer = useMemo(
      () => (svg?.closest(".full-map-container") as HTMLElement | null) ?? null,
      [svg]
    );

    const customParkingVerbiage = useCustomParkingVerbiage();
    const editMenu = useMemo(() => {
      const menu: { text: string; onClick: () => void }[] = [];
      if (res) {
        menu.push({
          text: `Edit ${getResourceTypeName(res, customParkingVerbiage)}`,
          onClick: openEditModal
        });
      }
      if (editGroupId && svg) {
        menu.push({
          text: "Assign Node Resource",
          onClick: openEditMapPointModal
        });
      }

      return menu;
    }, [
      res,
      editGroupId,
      openEditMapPointModal,
      openEditModal,
      svg,
      customParkingVerbiage
    ]);

    const closeEditMapModal = useCallback(() => {
      setEditMapPointModal(false);
      setEditGroupId(null);
      setTimeout(forceReloadChrome96Bug, 500);
    }, [setEditMapPointModal, setEditGroupId]);

    const schedules = res?.schedules?.length ? res?.schedules : [];
    const rawEditResource = resources.find((x) => x.id === res?.id);

    // `res` comes from the schedulable payload, which carries no visible
    // assignments — take them from the office resources list the map already
    // uses for its assigned-user avatars, so the card names the same owner.
    const resourceWithAssignments = useMemo(
      () => (res ? withVisibleAssignments(res, resources) : null),
      [res, resources]
    );

    const amenities = React.useMemo(() => {
      return res?.tags?.filter((tag) => tag.type === "amenity") || [];
    }, [res?.tags]);

    const { isOfficePrivacyEnabled } = useSettings();
    const isOrgPrivacyEnabled = isOfficePrivacyEnabled(officeId);

    // Privacy hides who owns or occupies a resource, the same way the hover
    // card does: the click card must not be the way around it.
    const hidesIdentity =
      isOrgPrivacyEnabled &&
      !!res?.type &&
      shouldEnablePrivacyForResourceType(res.type);

    // The reason an owner gave for being away, and the names the desk was
    // handed to, are theirs and their managers' to read. Only the manager
    // side is decided here: DedicatedReleases recognises the desk's own owner
    // from the releases it loads, which name them on every day, released or
    // not.
    const canSeeReleaseDetails = resourcePermission;

    // Lookup for all possibly assignable resources
    // joining free resources (no parent_id) or resources
    // that belongs to a area that belongs to the Floor
    const { areas: childAreas } = useAreas(officeId, floor.id);
    const validParentIds = (childAreas?.elements ?? [])
      .map((x) => x.id)
      .concat([floor.id]);
    const assignableResources = resources.filter(
      (x) => validParentIds.includes(x.parent_id ?? "") || x.parent_id === null
    );
    const onEditResource = async (hasError: boolean) => {
      if (!hasError && rawEditResource) {
        queryClient.invalidateQueries({
          queryKey: ["organizations.resourcesAll", rawEditResource.office_id]
        });
        queryClient.invalidateQueries({
          queryKey: ["organizations.schedulable", rawEditResource.office_id]
        });
      }
    };

    const handleRestrictionAlert = (): ReactElement | undefined => {
      const hasRestrictionContent = (availabilityErrors?.length ?? 0) > 0;
      const showRBRestriction =
        isRBFeature(authenticatedUser) && res?.type === "meeting_room";
      const shouldShow =
        !!res &&
        // Walk-up resources are never booked in advance through this flow —
        // their occupancy is managed externally via check-in/check-out, so
        // "can't be reserved at the selected time" is meaningless for them.
        !res.is_walkup &&
        !allowReserve &&
        (hasRestrictionContent ||
          (!!res.is_dedicated && !res.dedicated_exception)) &&
        (!isRBFeature(authenticatedUser) || showRBRestriction);

      if (!shouldShow || !res) {
        return undefined;
      }

      return (
        <PopoverInfoBox icon={infoBoxIcon}>
          <RestrictionAlert
            bare
            resourceId={res.id}
            availabilityErrors={availabilityErrors}
            dedicated={
              res.is_dedicated && !res.dedicated_exception
                ? {
                    userName:
                      resourceWithAssignments?.visible_assignment?.name ??
                      resourceWithAssignments?.user_visible_assignments?.[0]
                        ?.name ??
                      null
                  }
                : null
            }
          />
        </PopoverInfoBox>
      );
    };

    const showQuickReserve = canQuickReserve({
      resource: res,
      allowReserve,
      isLiveMode: timeMode === "live"
    });

    const quickReserveMutation = useQuickReserve({
      onSuccess: () => {
        fullscreenMapDisclosure?.close();
        setOpenResourceModal(false);
      }
    });

    const isResourceOccupiedAtSelectedTime =
      !!res &&
      !allowReserve &&
      (availabilityErrors ?? []).some((x) =>
        timeRelatedErrorCodes.includes(x.reasonCode)
      );

    const handleReserveButton = (
      className?: string
    ): ReactElement | undefined => {
      if (res) {
        // A dedicated desk is never bookable — no Reserve and no "Find
        // Another Time" (there is no other time). Only the workplace
        // request entry point remains relevant. Walk-up resources are the
        // same: their occupancy is managed externally via the API, never
        // through the app's reservation flow.
        // ...unless the desk is released for the day while its owner is
        // away: then it is booked like any other desk.
        const isDedicated = !!res.is_dedicated && !res.dedicated_exception;
        const isWalkUp = !!res.is_walkup;
        const hideReserveActions = isDedicated || isWalkUp;
        return (
          <div className="flex flex-wrap items-center gap-2">
            {!hideReserveActions && (
              <Button
                onClick={() => {
                  fullscreenMapDisclosure?.close();
                  if (isTicketGated) {
                    openGatedBooking();
                    return;
                  }
                  setOpenResourceModal(false);
                  openReservation(res);
                }}
                variant={
                  isResourceOccupiedAtSelectedTime && !isTicketGated
                    ? "outline"
                    : "default"
                }
                iconLeft={
                  isResourceOccupiedAtSelectedTime &&
                  !isTicketGated ? undefined : (
                    <Calendar className="h-4 w-4" />
                  )
                }
                className={cn("flex-1", className)}
              >
                {/* A gated resource has no direct booking flow, so there is no
                    "other time" to send someone to — it is always the request. */}
                {isTicketGated
                  ? "Request to Book"
                  : isResourceOccupiedAtSelectedTime
                    ? "Find Another Time"
                    : res.is_approvable
                      ? "Request to Book"
                      : "Reserve"}
              </Button>
            )}
            {!hideReserveActions && !isTicketGated && showQuickReserve && (
              <QuickReserveButton
                isLoading={quickReserveMutation.isPending}
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  if (res) {
                    quickReserveMutation.mutate({
                      resource: res,
                      date: startDate
                    });
                  }
                }}
              />
            )}
            {workplaceRequestsEnabled && (
              <Button
                variant="outline"
                iconLeft={<Plus className="h-4 w-4" />}
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  openNewRequest();
                }}
              >
                New request
              </Button>
            )}
          </div>
        );
      }

      return undefined;
    };

    return (
      <>
        <ResourceSettingsPanel
          isOpen={isSideDrawerOpen}
          onOpenChange={setIsSideDrawerOpen}
          onEdit={onEditResource}
        />

        <Modal
          title="Edit Map Resource"
          isOpen={editMapPointModal}
          onClose={closeEditMapModal}
          closeOnOverlayClick={false}
          size="md"
        >
          <EditMapResourceModal
            setMapChanges={setMapChanges}
            onClose={closeEditMapModal}
            editGroupId={editGroupId}
            svg={svg}
            resources={assignableResources}
          />
        </Modal>
        <MapPopoverShell
          ref={setRootRef}
          id={"resource-details-popper"}
          style={isMd ? styles.popper : undefined}
          {...(isMd ? attributes.popper : undefined)}
          className={cn(
            !openResourceModal && "hidden",
            "z-50 rounded-[14px] border-0",
            !isMd && "fixed right-2.5 bottom-28 left-2.5"
          )}
          bodyClassName="mx-auto w-full max-w-[385px] px-[18px] pt-[15px] md:w-screen"
          footer={handleReserveButton()}
          footerBordered={false}
          footerClassName="px-[18px] pb-[18px]"
        >
          <div className="font-normal text-base text-neutral-800">
            <div className="flex w-full items-start justify-between gap-2">
              {res && (
                <div className={"w-full min-w-0"}>
                  <ResourceLabel
                    className={"w-full min-w-0"}
                    headingClassName={
                      "font-mona font-bold text-[20px] leading-[1.1] tracking-[-0.015em]"
                    }
                    infoWrapperClassName={"gap-0"}
                    hierarchyClassName={"mt-1 gap-1.5 text-[#778086]"}
                    resource={res}
                    key={res.id}
                    showAmenities={false}
                    allowFavorite={false}
                    imageSize={"md"}
                    hierarchyLeft={
                      <MapPin
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    }
                  />
                </div>
              )}

              <div className="flex shrink-0 items-center gap-1">
                {isMd && resourcePermission && editMenu.length && !embedded ? (
                  <OptionsMenu
                    editMenuItems={editMenu}
                    // Portaled so the menu isn't clipped by this popover's
                    // own `overflow-hidden` (MapPopoverShell) — most visible
                    // when the popover is small, e.g. an unassigned map node
                    // with no resource content besides this menu. Tagged with
                    // the click-outside hook's own default ignore class
                    // (`ignore-onclickoutside`, react-cool-onclickoutside)
                    // since a portaled menu item click would otherwise read
                    // as an "outside" click and close the popover first.
                    withPortal
                    contentClassName="ignore-onclickoutside"
                    triggerClassName="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent"
                    iconClassName="h-5 w-5 text-[#9aa4ab]"
                  />
                ) : null}
                {isMd && (
                  <button
                    className="rounded-md text-neutral-300 hover:text-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    onClick={() => {
                      closeModal();
                      setSelectedResource(null);
                    }}
                  >
                    <span className="sr-only">Close panel</span>
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
            {resourceWithAssignments && (
              <ResourceStatusBand
                status={getResourceStatus(
                  resourceWithAssignments,
                  hidesIdentity
                )}
                restricted={
                  isResourceRestricted(resourceWithAssignments) &&
                  !hasResourceAssignment(resourceWithAssignments)
                }
                className="-mx-[18px] mt-3"
              />
            )}
            {resourceWithAssignments &&
            hasResourceAssignment(resourceWithAssignments) &&
            // A desk an admin switched off isn't anyone's right now — listing
            // who it's restricted to under "UNAVAILABLE" only confuses.
            !isResourceOutOfService(resourceWithAssignments, resources) ? (
              <ResourceAssignments resource={resourceWithAssignments} />
            ) : null}
            {res?.is_dedicated ? (
              <DedicatedReleases
                resourceId={res.id}
                enabled={openResourceModal}
                canSeeDetails={canSeeReleaseDetails}
                timeZone={officeTimeZone}
              />
            ) : null}
            {res?.description ? (
              <PopoverInfoBox className="max-h-[200px] overflow-y-auto">
                <Spoiler maxHeight={175} showLabel="Show more" hideLabel="Hide">
                  <TextEditorContent value={res.description} />
                </Spoiler>
              </PopoverInfoBox>
            ) : null}
            {res?.is_approvable ? (
              <PopoverInfoBox icon={infoBoxIcon}>
                {isTicketGated
                  ? "This space is booked by submitting a request. You'll be notified once it's approved."
                  : "Your request goes to the workspace admin. You'll be notified once it's approved."}
              </PopoverInfoBox>
            ) : null}
            {amenities?.length > 0 && (
              <ResourceAmenityChips
                amenities={amenities}
                className="flex flex-wrap gap-1.5 pt-3"
              />
            )}
            <div>
              {res?.schedules && schedules?.length ? (
                <div className="mt-2">
                  <div className="-mx-[18px] mb-2" />
                  <h5 className="font-semibold text-[#8b949b] text-[11px] uppercase tracking-[0.06em]">
                    RESERVATIONS
                  </h5>
                  <SchedulesGroups
                    schedules={res.schedules}
                    fullVersion={true}
                    isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                  />
                </div>
              ) : null}
              {isMd &&
                res &&
                res.available_slots > 0 &&
                (res.reserved_slots ? (
                  <>
                    <span className="ml-2 rounded-full px-2.5 py-0.5 font-medium text-xs">
                      Reserved {res.reserved_slots}
                    </span>
                  </>
                ) : null)}
            </div>

            {res ? (
              <OpenHourRulesNotice
                officeId={res.office_id}
                resourceId={res.id}
                date={format(startDate, "yyyy-MM-dd")}
              />
            ) : null}
            {handleRestrictionAlert()}
          </div>
        </MapPopoverShell>

        {newRequestResource ? (
          <React.Suspense fallback={null}>
            <NewRequestWizard
              isOpen={isNewRequestOpen}
              onOpenChange={setIsNewRequestOpen}
              contained={!!mapContainer}
              portalContainer={mapContainer}
              resource={
                {
                  id: newRequestResource.id,
                  name: newRequestResource.label || newRequestResource.name,
                  officeId: newRequestResource.office_id,
                  floorName: floor?.name,
                  type: newRequestResource.type
                } satisfies NewRequestResourceContext
              }
              allowedTypeIds={
                isGatedBooking
                  ? (newRequestResource.ticket_type_ids ?? [])
                  : undefined
              }
              // The date and time the map is showing, so a reservation
              // question starts on the slot the requester was looking at.
              reservationWhen={{
                days: [startDate],
                start: startDate,
                end: endDate
              }}
            />
          </React.Suspense>
        ) : null}
      </>
    );
  }
);
